import styled from 'styled-components';
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import { device } from '../../../utils/styled-component-mixins';
import ButtonLink from '../../shared/ButtonLinkComponent';

const RegisteredUserSubscriptionManagementFormStyled = styled.div`
  .alert {
    margin-left: 1rem;
    position: fixed;
    right: 1rem;
    top: 8rem;
    z-index: 100;

    @media ${device.desktop} {
      right: 2rem;
      top: 15rem;
    }
  }

  .btn-primary {
    margin-bottom: 5rem;
  }

  legend {
    font-size: 2.4rem;
    font-weight: 800;
    margin-bottom: 1rem;
  }

  #unsubscribe-label {
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 2.4rem;
    margin-top: 0;

    @media ${device.desktop} {
      font-size: 2rem;
      line-height: 3.2rem;
    }
  }
`;

const AvailableOptionsListStyled = styled.div`
  .option-group {
    align-items: center;
    display: flex;
    line-height: 1;

    @media ${device.desktop} {
      line-height: unset;
    }

    label {
      line-height: 2rem;
    }

    @media ${device.desktop} {
      line-height: unset;
    }

    input {
      flex-shrink: 0;
      margin-right: 1rem;
    }
  }

  fieldset {
    border: 0;
    display: grid;
    margin: 0 0 3rem 0;
    padding: 0;

    grid-row-gap: 0.8rem;

    @media ${device.desktop} {
      ${(props) =>
        props.numberOfOptions > 21 &&
        `grid-template-rows: repeat(${Math.ceil(props.numberOfOptions / 3)}, 1fr);`}
      ${(props) =>
        props.numberOfOptions < 21 && props.numberOfOptions > 6
          ? `grid-template-rows: repeat(7, 1fr);`
          : ''}
      ${(props) =>
        props.numberOfOptions > 7
          ? `grid-template-columns: repeat(3, 33.33%); grid-auto-flow: column;`
          : `grid-template-columns: 33.33%;`}
    }
  }
`;

const RegisteredUserSubscriptionManagementForm = (props) => {
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [snackbarVariant, setSnackbarVariant] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState(props.successMessage);
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    fetch('/api/GetAvailableSubscriptions')
      .then((response) => response.json())
      .then((data) => {
        const categories = [];

        data.forEach((item) => {
          if (!categories.find((category) => category.categoryName === item.category.displayName)) {
            categories.push({
              categoryName: item.category.displayName,
              availableSubscriptions: []
            });
          }
        });

        data.forEach((item) => {
          categories.forEach((category) => {
            if (category.categoryName === item.category.displayName) {
              category.availableSubscriptions.push(item);
            }
          });
        });
        setAvailableSubscriptions(categories);
      });
  }, []);

  useEffect(() => {
    fetch(`/api/GetLeadSubscriptionsAsync/`)
      .then((response) => response.json())
      .then((data) => {
        const subscriptions = [];
        data.forEach((subs) => {
          if (subs.value.toLowerCase() === 'opt-in') {
            subscriptions.push(subs.id.toString());
          }
        });
        setUserSubscriptions(subscriptions);
      });
  }, []);

  const updateSubscriptions = (optionId) => {
    if (userSubscriptions.length > 0 && userSubscriptions?.includes(optionId.toString())) {
      setUserSubscriptions(() =>
        userSubscriptions.filter((option) => option !== optionId.toString())
      );
    } else {
      setUserSubscriptions((arr) => [...arr, optionId.toString()]);
    }
  };

  const showAlert = (message, variant) => {
    setSnackbarMessage(message);
    setSnackbarVariant(variant);
    setShowSnackbar(true);

    setTimeout(() => {
      setShowSnackbar(false);
    }, 5000);
  };

  const saveChanges = (e) => {
    e.preventDefault();
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append(
      'RequestVerificationToken',
      document.getElementsByName('__RequestVerificationToken')[0].value
    );
    fetch('/api/ManageSubscriptions', {
      method: 'POST',
      body: JSON.stringify({
        subscriptionIds: userSubscriptions
      }),
      headers
    })
      .then(() => {
        showAlert(props.successMessage, 'success');
      })
      .catch((error) => {
        showAlert(props.errorMessage + error, 'error');
      });
  };

  const unsubscribeFromAll = (e) => {
    // eslint-disable-next-line no-alert, no-restricted-globals
    const isUnsubscribeConfirmed = confirm(props.confirmUnsubscribeMessage);
    e.preventDefault();
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append(
      'RequestVerificationToken',
      document.getElementsByName('__RequestVerificationToken')[0].value
    );
    if (isUnsubscribeConfirmed) {
      fetch('/api/ManageSubscriptions', {
        method: 'POST',
        body: JSON.stringify({ subscriptionIds: [] }),
        headers
      })
        .then(() => {
          showAlert(props.successMessage, 'success');
          setUserSubscriptions([]);
        })
        .catch((error) => {
          showAlert(props.errorMessage + error, 'error');
        });
    }
  };

  return (
    <RegisteredUserSubscriptionManagementFormStyled>
      {availableSubscriptions.length > 0 && (
        <>
          {availableSubscriptions.map((category) => (
            <AvailableOptionsListStyled
              key={category.categoryName}
              numberOfOptions={category.availableSubscriptions.length}
            >
              <fieldset>
                <legend>{category.categoryName}</legend>
                {category.availableSubscriptions.map((option) => (
                  <div className="option-group" key={option.id}>
                    <input
                      type="checkbox"
                      name={option.id}
                      id={option.id}
                      checked={userSubscriptions.includes(option.id.toString())}
                      onChange={() => updateSubscriptions(option.id)}
                    />
                    <label htmlFor={option.id}>{option.displayName}</label>
                  </div>
                ))}
              </fieldset>
            </AvailableOptionsListStyled>
          ))}
          <ButtonLink
            text={props.saveButtonText}
            isButtonTag={true}
            onClickFunction={saveChanges}
            type="primary"
          />

          <p id="unsubscribe-label">{props.unsubscribeLabel}</p>
          <ButtonLink
            ariaLabelledBy="unsubscribe-label"
            text={props.unsubscribeButtonText}
            isButtonTag={true}
            onClickFunction={unsubscribeFromAll}
            type="secondary"
          />
          <Alert show={showSnackbar} variant={snackbarVariant}>
            {snackbarMessage}
          </Alert>
        </>
      )}
    </RegisteredUserSubscriptionManagementFormStyled>
  );
};

export default RegisteredUserSubscriptionManagementForm;
